import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD } from "@/lib/structured-data";
import { MarketingShell, MarketingHero, FeatureGrid, CtaBand } from "@/components/MarketingShell";

export const Route = createFileRoute("/dispatch")({
  component: DispatchPage,
  head: () => ({
    meta: [
      { title: "Security Guard Dispatch Software — MissionReady" },
      { name: "description", content: "Security guard dispatch software: assign jobs on a live map, escalate SOS alerts, track response times, and document guard operations in one console." },
      ...socialMeta({
        title: "Security Guard Dispatch Software — MissionReady",
        description: "Security guard dispatch software with live-map assignments, SOS escalation, response-time tracking, and automated documentation.",
        url: "https://missionready.dev/dispatch",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/dispatch" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "MissionReady Dispatch",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web, iOS, Android",
        description: "Security guard dispatch software with a live dispatch console, GPS tracking, SOS escalation, and response-time analytics.",
        url: "https://missionready.dev/dispatch",
        brand: { "@type": "Brand", name: "MissionReady" },
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD", url: "https://missionready.dev/pricing" },
        featureList: [
          "Live dispatch console with map",
          "SOS and duress alerts",
          "Response-time tracking",
          "Geofenced auto-dispatch",
          "Two-way messaging",
          "Smart alert routing",
        ],
      }),
    }],
  }),
});

const FEATURES = [
  { icon: "📡", title: "Live dispatch console", desc: "See every officer, shift, and active call on a single live map. Assign and reassign jobs in seconds." },
  { icon: "🆘", title: "SOS & duress alerts", desc: "One-tap officer SOS with live location, hold-to-cancel, and supervisor escalation workflow." },
  { icon: "📈", title: "Response-time tracking", desc: "Measure dispatch-to-acknowledge and dispatch-to-on-scene, broken down by site, officer, and time of day." },
  { icon: "🛰️", title: "Geofenced auto-dispatch", desc: "Trigger jobs from alarm panels, IoT sensors, or geofence breaches — routed to the closest available officer." },
  { icon: "💬", title: "Two-way messaging", desc: "In-app channels for dispatch, supervisors, and field teams with unread badges and mute controls." },
  { icon: "🔔", title: "Smart alert routing", desc: "Severity, site, and on-call schedule drive who gets paged — push, SMS, or email." },
];

const HOW_IT_WORKS = [
  {
    title: "A call, alarm, or geofence event comes in",
    desc: "Jobs enter the console from your dispatchers, connected alarm panels, or automated geofence triggers. Each job carries its site, priority, and required post details.",
  },
  {
    title: "The console finds the right officer",
    desc: "MissionReady shows who is on duty, where they are right now, and who is closest to the site. Assign with one click — the officer's phone alerts instantly.",
  },
  {
    title: "Officers acknowledge and respond",
    desc: "Officers accept the job, navigate to the site, and mark themselves on scene. Every step is timestamped, so you can prove response times to clients.",
  },
  {
    title: "Everything is documented automatically",
    desc: "The dispatch record, messages, and response metrics are stored against the site and shift — ready for client reporting without re-typing a word.",
  },
];

function DispatchPage() {
  return (
    <MarketingShell>
      <MarketingHero
        eyebrow="Guard Operations"
        title={<>Security guard <span className="text-primary">dispatch software.</span></>}
        subtitle="Assign jobs on a live map, escalate SOS alerts, and prove response times — purpose-built for security teams, not generic field service."
      />
      <FeatureGrid
        items={FEATURES}
        heading="Security guard dispatch capabilities"
        subheading="Everything a security dispatcher needs to get the right officer to the right site — and prove it happened."
      />

      <section className="mr-capabilities">
        <div className="mr-shell">
          <div className="mr-section-heading">
            <h2>How security dispatch works in MissionReady</h2>
          </div>
          <p className="mr-section-intro">
            From the moment a call comes in to the moment the job closes, every step is tracked in one console.
          </p>
          <div className="mr-capability-grid">
            {HOW_IT_WORKS.map((step, i) => (
              <article key={step.title}>
                <span aria-hidden="true">{i + 1}.</span>
                <h2>{step.title}</h2>
                <p>{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mr-capabilities">
        <div className="mr-shell">
          <div className="mr-section-heading">
            <h2>Why teams choose dedicated security guard dispatch software</h2>
          </div>
          <p className="mr-section-intro">
            Generic field-service tools weren't designed for guard operations. Security dispatch has its own demands:
            officers working alone at night, clients who expect documented response times, and emergencies where
            seconds matter. MissionReady's dispatch console is built around those realities — live officer locations,
            one-tap SOS escalation, and automatic documentation that stands up to client audits and insurance reviews.
          </p>
          <p className="mr-section-intro">
            Because dispatch lives in the same platform as the rest of your operation, nothing falls between systems.
            Jobs connect directly to{" "}
            <Link to="/scheduling">guard scheduling</Link> so you only see on-duty officers, to{" "}
            <Link to="/patrol">patrol and checkpoint tours</Link> so patrols and dispatches share one map, and to{" "}
            <Link to="/incident-reporting">incident reporting</Link> so a dispatched response can become a
            client-ready report in one tap. It's part of MissionReady's complete{" "}
            <Link to="/security-guard-management-software">security guard management software</Link> platform — see how it compares in our{" "}
            <Link to="/compare">head-to-head comparisons</Link> or review{" "}
            <Link to="/pricing">pricing</Link>.
          </p>
        </div>
      </section>

      <CtaBand title="Spin up your dispatch console today" subtitle="Free 30-day trial. No credit card. Cancel anytime." />
    </MarketingShell>
  );
}
